import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';
import { OrderedSet } from 'immutable';

export type PersistenceState = {
	recentJobId: string | null;
	isPersisted: boolean;
	date: Date | null;
	count: number;
	duration: number;
	size: number;
	isDefault: boolean;
};

export type ParentProject = {
	label: string;
	charCount: number;
	depotCount: number;
};

interface IEntityItemEntity {
	id: number;
	code: string;
	label: string;
	description: string;
	url: string;
	isImported: boolean;
	tags: OrderedSet<AppTagItemEntity>;
	isExposed: boolean;
	persistenceState: PersistenceState;
	parentProject: ParentProject;
	isActive: boolean;
	pictogramUrl: string;
	isActionInProgress: boolean;
	updateDate: Date | null;
	creationDate: Date | null;
	updatedBy: string;
	createdBy: string;
	updatedByAvatar: AvatarEntity | null;
	createdByAvatar: AvatarEntity | null;
}

const entityDefaults: IEntityItemEntity = {
	id: -1,
	label: '',
	code: '',
	description: '',
	url: '',
	isImported: false,
	tags: OrderedSet(),
	updateDate: null,
	updatedBy: '',
	creationDate: null,
	createdBy: '',
	isExposed: false,
	parentProject: {
		label: '',
		charCount: 0,
		depotCount: 0,
	},
	persistenceState: {
		recentJobId: null,
		isPersisted: false,
		count: 0,
		duration: 0,
		size: 0,
		date: null,
		isDefault: true,
	},
	isActive: false,
	updatedByAvatar: null,
	createdByAvatar: null,
	pictogramUrl: '',
	isActionInProgress: false,
};

export class EntityItemEntity implements IEntityItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public code = entityDefaults.code;
	public description = entityDefaults.description;
	public url = entityDefaults.url;
	public label = entityDefaults.label;
	public tags = entityDefaults.tags;
	public updateDate = entityDefaults.updateDate;
	public updatedBy = entityDefaults.updatedBy;
	public creationDate = entityDefaults.creationDate;
	public createdBy = entityDefaults.createdBy;
	public isExposed = entityDefaults.isExposed;
	public isActive = entityDefaults.isActive;
	public isImported = entityDefaults.isImported;
	public updatedByAvatar = entityDefaults.updatedByAvatar;
	public createdByAvatar = entityDefaults.createdByAvatar;
	public pictogramUrl = entityDefaults.pictogramUrl;
	public isActionInProgress = entityDefaults.isActionInProgress;
	public parentProject: ParentProject = {
		label: entityDefaults.parentProject.label,
		depotCount: entityDefaults.parentProject.depotCount,
		charCount: entityDefaults.parentProject.charCount,
	};
	public persistenceState: PersistenceState = {
		recentJobId: entityDefaults.persistenceState.recentJobId,
		count: entityDefaults.persistenceState.count,
		date: entityDefaults.persistenceState.date,
		duration: entityDefaults.persistenceState.duration,
		isPersisted: entityDefaults.persistenceState.isPersisted,
		size: entityDefaults.persistenceState.size,
		isDefault: entityDefaults.persistenceState.isDefault,
	};

	public static build(
		params: Partial<IEntityItemEntity> = {}
	): EntityItemEntity {
		const entity = new EntityItemEntity();
		entity.id = params.id ?? entityDefaults.id;
		entity.code = params.code ?? entityDefaults.code;
		entity.description = params.description ?? entityDefaults.description;
		entity.label = params.label ?? entityDefaults.label;
		entity.url = params.url ?? entityDefaults.url;
		entity.isImported = params.isImported ?? entityDefaults.isImported;
		entity.parentProject = params.parentProject ?? entityDefaults.parentProject;
		entity.tags = params.tags ?? entityDefaults.tags;
		entity.updateDate = params.updateDate ?? entityDefaults.updateDate;
		entity.updatedBy = params.updatedBy ?? entityDefaults.updatedBy;
		entity.creationDate = params.creationDate ?? entityDefaults.creationDate;
		entity.createdBy = params.createdBy ?? entityDefaults.createdBy;
		entity.isExposed = params.isExposed ?? entityDefaults.isExposed;
		entity.persistenceState =
			params.persistenceState ?? entity.persistenceState;
		entity.isActive = params.isActive ?? entityDefaults.isActive;
		entity.createdByAvatar =
			params.createdByAvatar ?? entityDefaults.createdByAvatar;
		entity.isActionInProgress =
			params.isActionInProgress ?? entityDefaults.isActionInProgress;
		entity.pictogramUrl = params.pictogramUrl ?? entityDefaults.pictogramUrl;
		if (params.updatedBy) {
			entity.updatedByAvatar = AvatarEntity.build(params.updatedBy);
		}
		if (params.createdBy) {
			entity.createdByAvatar = AvatarEntity.build(params.createdBy);
		}
		return entity;
	}

	public setJobInProgress(value: boolean): EntityItemEntity {
		return produce(this, (draft: EntityItemEntity) => {
			draft.isActionInProgress = value;
		});
	}

	public setJobId(value: string | null): EntityItemEntity {
		return produce(this, (draft: EntityItemEntity) => {
			draft.persistenceState = {
				...draft.persistenceState,
				recentJobId: value,
			};
		});
	}

	public setPersistenceInfo(info: {
		isPersisted: boolean;
		partitionCount: number;
		persistenceDate: Date | null;
		persistenceDuration: number;
		persistenceSize: number;
	}): EntityItemEntity {
		return produce(this, (draft: EntityItemEntity) => {
			draft.persistenceState.isPersisted = info.isPersisted;
			draft.persistenceState.count = info.partitionCount;
			draft.persistenceState.date = info.persistenceDate;
			draft.persistenceState.duration = info.persistenceDuration;
			draft.persistenceState.size = info.persistenceSize;
			draft.isActionInProgress = false;
		});
	}

	public setCharCount(count: number): EntityItemEntity {
		return produce(this, (draft: EntityItemEntity) => {
			draft.parentProject.charCount = count;
		});
	}

	public setDepotCount(count: number): EntityItemEntity {
		return produce(this, (draft: EntityItemEntity) => {
			draft.parentProject.depotCount = count;
		});
	}

	public setOnGoingPersistence(): EntityItemEntity {
		return produce(this, (draft) => {
			draft.isActionInProgress = true;
		});
	}

	public updateEntityPersistenceState(
		persistenceState: PersistenceState
	): EntityItemEntity {
		return produce(this, (draft) => {
			draft.persistenceState = persistenceState;
		});
	}
}
